import React, { useState, useEffect } from 'react';
import { getMonthlyTitles } from '../firebase/api';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import UserHeader from './UserHeader';
import { useAuth } from '../firebase/AuthContext';
import './MantraAdmin.css';

const MonthlyTitleView = ({ onBack }) => {
  const { sessionId } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [monthlyTitles, setMonthlyTitles] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingMonthlyTitles, setLoadingMonthlyTitles] = useState(false);
  const [posterIdMap, setPosterIdMap] = useState({}); // poster_title -> poster_id のマッピング

  // ユーザー一覧を取得
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        // monthly_titleコレクションからユニークなuser_idを取得
        const querySnapshot = await getDocs(collection(db, 'monthly_title'));
        const userIds = new Set();
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.user_id) {
            userIds.add(data.user_id);
          }
        });
        
        // user_idのリストを取得（必要に応じてusersコレクションから詳細情報を取得）
        const userList = Array.from(userIds).map(userId => ({
          id: userId,
          displayName: userId // デフォルトはuser_idを表示
        }));
        
        // usersコレクションから詳細情報を取得（存在する場合）
        try {
          const usersSnapshot = await getDocs(collection(db, 'users'));
          const usersMap = {};
          usersSnapshot.forEach((doc) => {
            const userData = doc.data();
            usersMap[doc.id] = userData;
          });
          
          // user_idに対応するユーザー情報があれば表示名を更新
          userList.forEach(user => {
            if (usersMap[user.id]) {
              user.displayName = usersMap[user.id].displayName || usersMap[user.id].nickname || user.id;
            }
          });
        } catch (error) {
          console.warn('Users collection not found or error:', error);
        }
        
        setUsers(userList.sort((a, b) => a.displayName.localeCompare(b.displayName)));
        setLoadingUsers(false);
      } catch (error) {
        console.error('Error fetching users:', error);
        alert('ユーザー一覧取得に失敗しました: ' + error.message);
        setLoadingUsers(false);
      }
    };
    
    fetchUsers();
  }, []);

  // 選択されたユーザーのmonthly_titleを取得
  useEffect(() => {
    if (selectedUserId) {
      fetchMonthlyTitles(selectedUserId);
    } else {
      setMonthlyTitles([]);
    }
  }, [selectedUserId, sessionId]);

  const fetchMonthlyTitles = async (userId) => {
    try {
      setLoadingMonthlyTitles(true);
      if (!sessionId) {
        throw new Error('セッションIDがありません');
      }
      
      const response = await getMonthlyTitles(sessionId);
      // getMonthlyTitlesの戻り値は配列
      const allTitles = Array.isArray(response) ? response : [];
      
      // 選択されたユーザーのmonthly_titleのみをフィルタリング
      const userTitles = allTitles
        .filter(title => title.user_id === userId)
        .sort((a, b) => {
          // from_year_monthで降順ソート
          const aFrom = a.from_year_month || 0;
          const bFrom = b.from_year_month || 0;
          return bFrom - aFrom;
        });
      
      setMonthlyTitles(userTitles);
      
      // ポスターIDのマッピングを取得
      await fetchPosterIds(userTitles);
      
      setLoadingMonthlyTitles(false);
    } catch (error) {
      console.error('Error fetching monthly titles:', error);
      alert('月次タイトル取得に失敗しました: ' + error.message);
      setLoadingMonthlyTitles(false);
    }
  };

  // ポスターIDのマッピングを取得
  const fetchPosterIds = async (titles) => {
    try {
      const posterTitleSet = new Set();
      
      // すべてのbest_selectionsからposter_titleを収集
      titles.forEach(title => {
        if (title.best_selections && Array.isArray(title.best_selections)) {
          title.best_selections.forEach(selection => {
            if (selection.poster_title) {
              posterTitleSet.add(selection.poster_title);
            }
          });
        }
      });
      
      if (posterTitleSet.size === 0) {
        setPosterIdMap({});
        return;
      }
      
      // Firestoreからポスターを取得
      const postersSnapshot = await getDocs(collection(db, 'posters'));
      const idMap = {};
      
      postersSnapshot.forEach((doc) => {
        const posterData = doc.data();
        // title, title2, title3を結合してposter_titleを作成
        const fullTitle = (posterData.title || '') + (posterData.title2 || '') + (posterData.title3 || '');
        
        // 完全一致または部分一致をチェック
        posterTitleSet.forEach(posterTitle => {
          if (fullTitle.includes(posterTitle) || posterTitle.includes(fullTitle)) {
            idMap[posterTitle] = doc.id;
          }
        });
      });
      
      setPosterIdMap(idMap);
    } catch (error) {
      console.error('Error fetching poster IDs:', error);
      setPosterIdMap({});
    }
  };

  // 期間タイトルを生成
  const getPeriodTitle = (titleDoc) => {
    if (titleDoc.from_year_month && titleDoc.to_year_month) {
      const from = parseYearMonth(titleDoc.from_year_month);
      const to = parseYearMonth(titleDoc.to_year_month);
      if (from && to) {
        return `${from.year}年${from.month}月から${to.year}年${to.month}月`;
      }
    }
    return '期間未設定';
  };

  const parseYearMonth = (yyyymm) => {
    if (!yyyymm) return null;
    const str = String(yyyymm);
    if (str.length !== 6) return null;
    const year = parseInt(str.substring(0, 4), 10);
    const month = parseInt(str.substring(4, 6), 10);
    return { year, month };
  };

  return (
    <div className="mantra-admin">
      <UserHeader />
      <div className="mantra-admin-container">
        <div className="mantra-admin-header">
          <button 
            className="mantra-admin-back-btn"
            onClick={onBack}
          >
            ← TOPに戻る
          </button>
          <h1>月次タイトル一覧</h1>
        </div>

        {/* ユーザー選択 */}
        {!selectedUserId && (
          <div className="mantra-admin-user-selection">
            <h2>ユーザーを選択してください</h2>
            {loadingUsers ? (
              <div className="mantra-admin-loading">読み込み中...</div>
            ) : users.length === 0 ? (
              <div className="mantra-admin-no-posters">ユーザーがありません</div>
            ) : (
              <div className="mantra-admin-user-list">
                {users.map((user) => (
                  <button
                    key={user.id}
                    className="mantra-admin-user-item"
                    onClick={() => setSelectedUserId(user.id)}
                  >
                    {user.displayName}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 選択されたユーザーのmonthly_title表示 */}
        {selectedUserId && (
          <div className="mantra-admin-monthly-titles">
            <div className="mantra-admin-selected-user">
              <button
                className="mantra-admin-back-btn"
                onClick={() => setSelectedUserId(null)}
              >
                ← ユーザー選択に戻る
              </button>
              <h2>ユーザー: {users.find(u => u.id === selectedUserId)?.displayName || selectedUserId}</h2>
              <p style={{ marginTop: '5px', fontSize: '14px', color: '#666' }}>
                ユーザーID: {selectedUserId}
              </p>
            </div>

            {loadingMonthlyTitles ? (
              <div className="mantra-admin-loading">読み込み中...</div>
            ) : monthlyTitles.length === 0 ? (
              <div className="mantra-admin-no-posters">月次タイトルがありません</div>
            ) : (
              <div className="mantra-admin-monthly-titles-list">
                {monthlyTitles.map((titleDoc) => (
                  <div key={titleDoc.id} className="mantra-admin-monthly-title-card">
                    <div className="mantra-admin-monthly-title-header">
                      <h2>{getPeriodTitle(titleDoc)}</h2>
                    </div>
                    {titleDoc.best_selections && titleDoc.best_selections.length > 0 && (
                      <div className="mantra-admin-best-selections">
                        {titleDoc.best_selections.map((selection, idx) => (
                          <div key={idx} className="mantra-admin-selection-item">
                            <h3 className="mantra-admin-selection-category">
                              {selection.category}
                            </h3>
                            {selection.poster_title && (
                              <>
                                {posterIdMap[selection.poster_title] && (
                                  <p className="mantra-admin-selection-poster-id">
                                    <strong>ポスターID:</strong> {posterIdMap[selection.poster_title]}
                                  </p>
                                )}
                                <p className="mantra-admin-selection-poster-title">
                                  <strong>ポスタータイトル:</strong> {selection.poster_title}
                                </p>
                              </>
                            )}
                            {selection.content && (
                              <p className="mantra-admin-selection-content">
                                <strong>内容:</strong> {selection.content}
                              </p>
                            )}
                            {!selection.poster_title && !selection.content && (
                              <p className="mantra-admin-selection-empty">該当なし</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyTitleView;
